import { useState, type FormEvent } from "react";
import type { Business } from "@/types/directory";
import { submitLead } from "@/services/directoryService";

interface BusinessContactCardProps {
  business: Business;
}

export default function BusinessContactCard({ business }: BusinessContactCardProps) {
  const todayIndex = (new Date().getDay() + 6) % 7;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    business.address,
  )}`;

  // Enquiry form state
  const [enquiryOpen,    setEnquiryOpen]    = useState(false);
  const [enquiryName,    setEnquiryName]    = useState("");
  const [enquiryPhone,   setEnquiryPhone]   = useState("");
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [enquiryStatus,  setEnquiryStatus]  = useState<"idle"|"submitting"|"success"|"error">("idle");
  const [enquiryError,   setEnquiryError]   = useState("");

  async function handleEnquiry(e: FormEvent) {
    e.preventDefault();
    setEnquiryStatus("submitting");
    setEnquiryError("");
    try {
      const bizId = parseInt(business.id, 10);
      await submitLead({
        formType:   "enquiry",
        name:       enquiryName,
        phone:      enquiryPhone,
        message:    enquiryMessage,
        businessId: isNaN(bizId) ? undefined : bizId,
        sourcePage: `/business/${business.slug}`,
      });
      setEnquiryStatus("success");
    } catch {
      setEnquiryStatus("error");
      setEnquiryError("Could not send your enquiry. Please try calling directly.");
    }
  }

  const profileComplete = [
    business.phone, business.whatsapp, business.email, business.website,
  ].filter(Boolean).length;

  return (
    <aside className="flex flex-col gap-6">
      {/* Map — sits above the contact information */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-[#F5F7FA]">
        <div className="border-b border-background-200 p-6 pb-4">
          <h2 className="font-heading text-base font-bold text-foreground-950">Location</h2>
          <p className="mt-1 text-sm text-foreground-600">{business.address}</p>
        </div>
        <iframe
          title={`Map showing ${business.name}`}
          src={business.mapEmbedUrl}
          className="h-56 w-full border-0"
          aria-label={`Map location of ${business.name}`}
          loading="lazy"
        />
      </div>

      {/* Contact info card */}
      <div className="rounded-lg border border-background-200 bg-[#F5F7FA] p-6">
        <h2 className="font-heading text-base font-bold text-foreground-950">Contact information</h2>

        <div className="mt-4 flex flex-col gap-3">
          {business.phone && (
            <a href={`tel:${business.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-3 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-phone-line text-base" /></span>
              {business.phone}
            </a>
          )}
          {business.whatsapp && (
            <a href={`https://wa.me/${business.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank" rel="nofollow noopener noreferrer"
              className="flex items-center gap-3 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm font-semibold text-secondary-800 transition-colors hover:bg-secondary-100">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-whatsapp-line text-base" /></span>
              {business.whatsapp}
            </a>
          )}
          {business.email && (
            <a href={`mailto:${business.email}`}
              className="flex items-center gap-3 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-mail-line text-base" /></span>
              {business.email}
            </a>
          )}
          {business.website && (
            <a href={business.website} target="_blank" rel="nofollow noopener noreferrer"
              className="flex items-center gap-3 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-global-line text-base" /></span>
              Visit website
            </a>
          )}
          <a href={directionsUrl} target="_blank" rel="nofollow noopener noreferrer"
            className="flex items-center gap-3 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">
            <span className="flex h-4 w-4 items-center justify-center"><i className="ri-navigation-line text-base" /></span>
            Get directions
          </a>
        </div>

        {/* Enquire Now toggle */}
        <div className="mt-4">
          {enquiryStatus === "success" ? (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <i className="ri-checkbox-circle-line text-base" />
              Enquiry sent! The business will get back to you soon.
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEnquiryOpen((o) => !o)}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={enquiryOpen ? "ri-close-line text-base" : "ri-mail-send-line text-base"} />
                </span>
                {enquiryOpen ? "Cancel" : "Send Enquiry"}
              </button>

              {enquiryOpen && (
                <form onSubmit={handleEnquiry} className="mt-4 flex flex-col gap-3">
                  <input
                    type="text" required placeholder="Your name *" value={enquiryName}
                    onChange={(e) => setEnquiryName(e.target.value)}
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none focus:border-primary-400"
                  />
                  <input
                    type="tel" required placeholder="Phone number *" value={enquiryPhone}
                    onChange={(e) => setEnquiryPhone(e.target.value)}
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none focus:border-primary-400"
                  />
                  <textarea
                    required rows={3} placeholder="Your message *"
                    maxLength={500} value={enquiryMessage}
                    onChange={(e) => setEnquiryMessage(e.target.value)}
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none focus:border-primary-400"
                  />
                  {enquiryError && (
                    <p className="text-xs text-rose-600">{enquiryError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={enquiryStatus === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {enquiryStatus === "submitting" ? (
                      <><i className="ri-loader-4-line animate-spin text-base" /> Sending…</>
                    ) : (
                      <><i className="ri-send-plane-line text-base" /> Send Enquiry</>
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <div className="mt-5 border-t border-background-200 pt-4">
          <p className="flex items-start gap-2.5 text-sm text-foreground-700">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-secondary-600">
              <i className="ri-map-pin-line text-base" />
            </span>
            {business.address}
          </p>
          {!business.website && (
            <p className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
                <i className="ri-information-line text-[13px]" />
              </span>
              {profileComplete} of 4 contact channels provided
            </p>
          )}
        </div>
      </div>

      {/* Working hours */}
      <div className="rounded-lg border border-background-200 bg-[#F5F7FA] p-6">
        <h2 className="font-heading text-base font-bold text-foreground-950">Working hours</h2>
        <ul className="mt-4 flex flex-col gap-1">
          {business.hours.map((entry, index) => {
            const isToday = index === todayIndex;
            return (
              <li key={entry.day}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${isToday ? "bg-accent-50 font-semibold" : ""}`}>
                <span className={isToday ? "text-foreground-950" : "text-foreground-600"}>
                  {entry.day}
                  {isToday && <span className="ml-2 text-xs text-accent-700">Today</span>}
                </span>
                {entry.closed ? (
                  <span className="text-foreground-400">Closed</span>
                ) : (
                  <span className={isToday ? "text-foreground-950" : "text-foreground-700"}>
                    {entry.open} – {entry.close}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}